import { ReactNode } from "react";

import { Center, Column, Row, Text } from "@/components";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { dates } from "@/lib/dates";
import { trophies as trophyFormatting } from "@/lib/trophies";

import { StafferTrophyLeader, TrophyAwardType } from "@/types";

export const TrophyHistoryDialog = ({
  description,
  isOpen,
  leaders,
  minimumScreenCount,
  onOpenChange,
  title,
  trophyArt,
  type,
}: {
  description: string;
  isOpen: boolean;
  leaders: StafferTrophyLeader[];
  minimumScreenCount?: number;
  onOpenChange: (isOpen: boolean) => void;
  title: string;
  trophyArt: ReactNode;
  type: TrophyAwardType;
}) => {
  /* Helpers */
  const formatValue = (leader?: StafferTrophyLeader): string => {
    if (!leader) return "-";
    return trophyFormatting.value(type, leader.value);
  };

  const formatRank = (rank: number): string => {
    if (rank === 1) return "1st";
    if (rank === 2) return "2nd";
    if (rank === 3) return "3rd";
    return `${String(rank)}th`;
  };

  const formatAwardedAt = (achievedAt?: string): string => {
    if (!achievedAt) return "-";
    const formattedDate = dates.formatIsoDateForUser(achievedAt);
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return formattedDate || "-";
  };

  /* Render */
  const render = () => {
    const winner = leaders.at(0);
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl gap-0 overflow-hidden border-amber-200 bg-amber-50 p-0 shadow-xl shadow-amber-200/40">
          <div className="grid h-80 grid-cols-[30%_1fr] border-b border-amber-300 bg-gradient-to-br from-amber-100 via-yellow-100 to-amber-200/80">
            <Center className="h-full w-full bg-[radial-gradient(circle,rgba(251,191,36,0.2)_0%,rgba(251,191,36,0.07)_42%,transparent_72%)]">
              <Center className="h-40 w-40 translate-x-10 [&_img]:max-h-full [&_img]:max-w-full [&_img]:object-contain">
                {trophyArt}
              </Center>
            </Center>
            <DialogHeader className="min-w-0 justify-center pr-12 text-left">
              <Column className="translate-x-11 items-start">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DialogTitle className="w-fit max-w-full cursor-default truncate rounded-full border border-amber-300 bg-white/45 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-amber-700">
                        {title}
                      </DialogTitle>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs border-amber-200 bg-amber-50">
                      <Text className="text-sm font-medium text-stone-600">
                        Awarded to the staffer who {description}
                      </Text>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Text className="mt-4 max-w-full break-words text-3xl font-bold leading-tight text-amber-950">
                  {winner
                    ? `${winner.firstName} ${winner.lastName}`
                    : "No winner yet"}
                </Text>
                <Text className="mt-2 bg-gradient-to-r from-amber-800 via-yellow-500 to-amber-700 bg-clip-text text-4xl font-bold leading-none text-transparent">
                  {formatValue(winner)}
                </Text>
                {winner?.screenCount !== undefined ? (
                  <Text className="mt-2 text-xs font-normal text-stone-600">
                    Median across {winner.screenCount.toLocaleString("en-US")}{" "}
                    first screens
                  </Text>
                ) : null}
                <Text className="mt-4 text-xs font-normal text-stone-500">
                  Awarded {formatAwardedAt(winner?.achievedAt)}
                </Text>
                {minimumScreenCount !== undefined ? (
                  <Text className="mt-2 text-[10px] font-normal text-stone-500">
                    *Minimum {minimumScreenCount.toLocaleString("en-US")}{" "}
                    screens in a month. Records from July 2026.
                  </Text>
                ) : null}
              </Column>
            </DialogHeader>
          </div>
          <Column className="gap-2 bg-amber-50/40 px-8 py-8">
            <Text className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-700/70">
              Other top performances
            </Text>
            <Column className="divide-y divide-amber-200/60">
              {leaders.slice(1).map((leader) => (
                <Row
                  key={leader.rank}
                  className="grid grid-cols-[48px_1fr_140px_120px] items-center px-1 py-3.5"
                >
                  <Center className="h-7 w-7 rounded-full bg-amber-100 text-xs font-semibold text-amber-800">
                    {formatRank(leader.rank)}
                  </Center>
                  <Column className="gap-0.5">
                    <Text className="text-sm font-semibold text-amber-950">
                      {leader.firstName} {leader.lastName}
                    </Text>
                    {leader.screenCount !== undefined ? (
                      <Text className="text-xs font-normal text-stone-500">
                        {leader.screenCount.toLocaleString("en-US")} first
                        screens
                      </Text>
                    ) : null}
                  </Column>
                  <Text className="text-sm font-medium text-amber-900/75">
                    {formatValue(leader)}
                  </Text>
                  <Text className="text-sm font-medium text-amber-900/75">
                    {formatAwardedAt(leader.achievedAt)}
                  </Text>
                </Row>
              ))}
            </Column>
          </Column>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};
